import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BatchGenerationPanel = ({ onGenerate, isGenerating, progress }) => {
  const [batchSize, setBatchSize] = useState(10);
  const [selectedClients, setSelectedClients] = useState([]);

  const pendingClients = [
    {
      id: 1,
      name: 'Anna Schmidt',
      dateOfBirth: '15.03.1985',
      address: 'Hauptstraße 45, 10115 Berlin',
      status: 'pending'
    },
    {
      id: 2,
      name: 'Thomas Müller',
      dateOfBirth: '22.07.1990',
      address: 'Bahnhofstraße 12, 80331 München',
      status: 'pending'
    },
    {
      id: 3,
      name: 'Maria Weber',
      dateOfBirth: '08.11.1978',
      address: 'Königsallee 89, 40212 Düsseldorf',
      status: 'pending'
    },
    {
      id: 4,
      name: 'Michael Fischer',
      dateOfBirth: '30.05.1992',
      address: 'Zeil 34, 60313 Frankfurt',
      status: 'pending'
    },
    {
      id: 5,
      name: 'Sarah Becker',
      dateOfBirth: '17.09.1988',
      address: 'Mönckebergstraße 7, 20095 Hamburg',
      status: 'pending'
    }
  ];

  const handleSelectAll = () => {
    if (selectedClients?.length === pendingClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(pendingClients?.map(c => c?.id));
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev =>
      prev?.includes(clientId)
        ? prev?.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Stapelverarbeitung</h3>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 text-accent rounded-md text-sm font-medium">
          <Icon name="Layers" size={16} />
          <span>{selectedClients?.length} ausgewählt</span>
        </div>
      </div>
      {!isGenerating ? (
        <div className="space-y-6">
          {/* Batch Size Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Stapelgröße</label>
            <div className="grid grid-cols-4 gap-3">
              {[10, 25, 50, 100]?.map((size) => (
                <button
                  key={size}
                  onClick={() => setBatchSize(size)}
                  className={`px-4 py-3 rounded-lg border font-medium transition-all ${
                    batchSize === size
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:border-primary/50 text-foreground'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Client Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground">Kunden auswählen</label>
              <button
                onClick={handleSelectAll}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                {selectedClients?.length === pendingClients?.length ? 'Alle abwählen' : 'Alle auswählen'}
              </button>
            </div>

            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-80 overflow-y-auto">
                {pendingClients?.map((client) => (
                  <label
                    key={client?.id}
                    className={`flex items-center gap-4 p-4 border-b border-border last:border-b-0 cursor-pointer transition-colors ${
                      selectedClients?.includes(client?.id) ? 'bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedClients?.includes(client?.id)}
                      onChange={() => handleSelectClient(client?.id)}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground">{client?.name}</h4>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span>Geb.: {client?.dateOfBirth}</span>
                        <span>•</span>
                        <span className="truncate">{client?.address}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1 bg-warning/10 text-warning rounded text-xs font-medium">
                      <Icon name="Clock" size={12} />
                      <span>Ausstehend</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Batch Options */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Settings" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-3">Stapelverarbeitungsoptionen</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-xs text-foreground">Automatische Fehlerbehandlung</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-xs text-foreground">Fortschrittsbenachrichtigungen senden</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      defaultChecked={false}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-xs text-foreground">Supervisor-Genehmigung erforderlich</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={() => onGenerate(selectedClients)}
            disabled={selectedClients?.length === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="Zap" size={20} />
            <span>Stapelgenerierung starten ({selectedClients?.length} Kunden)</span>
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Generierungsfortschritt</span>
              <span className="text-sm font-semibold text-primary">{progress?.current} / {progress?.total}</span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
                style={{ width: `${(progress?.current / progress?.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Geschätzte verbleibende Zeit: {progress?.estimatedTime}
            </p>
          </div>

          {/* Current Processing */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Icon name="Loader2" size={24} color="var(--color-primary)" className="animate-spin flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Verarbeitung läuft...</p>
                <p className="text-xs text-muted-foreground mt-1">{progress?.currentClient}</p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
              <Icon name="CheckCircle" size={24} color="var(--color-success)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{progress?.successful}</p>
              <p className="text-xs text-muted-foreground mt-1">Erfolgreich</p>
            </div>
            <div className="bg-error/5 border border-error/20 rounded-lg p-4 text-center">
              <Icon name="XCircle" size={24} color="var(--color-error)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{progress?.failed}</p>
              <p className="text-xs text-muted-foreground mt-1">Fehlgeschlagen</p>
            </div>
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 text-center">
              <Icon name="Clock" size={24} color="var(--color-warning)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{progress?.pending}</p>
              <p className="text-xs text-muted-foreground mt-1">Ausstehend</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchGenerationPanel;